import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { routes } from "@ckgo2/shared";
import { contentPageCreateSchema } from "@ckgo2/shared/schemas";
import { CONTENT_PAGE_STATUSES } from "@ckgo2/shared/types";
import type { ContentPage, ContentPageStatus } from "@ckgo2/shared/types";
import { WEB_URL } from "@/lib/webUrl";
import { useEditPage } from "@/hooks/useEditPage";
import DestinationPicker from "@/components/common/DestinationPicker";
import EditPageHeader from "@/components/common/EditPageHeader";
import MediaPicker from "@/components/common/MediaPicker";
import RichTextEditor from "@/components/editor/RichTextEditor";

interface ContentPageFormData {
  title: string;
  slug: string;
  perex: string;
  body: string;
  featuredImageId: string | null;
  /** "" = bez destinace; schéma ho převede na `null`. */
  destinationId: string;
  status: ContentPageStatus;
  metaTitle: string;
  metaDescription: string;
}

/**
 * Editor jednoduché stránky (řez 12, P16) — devět polí v témž tvaru jako ostatní obsahové
 * entity (destinace sdíleným výběrem `DestinationPicker`, jen zemská úroveň). Adresní název se
 * generuje z názvu.
 */
export default function ContentPageEditPage() {
  const { t } = useTranslation();

  const { isNew, isLoading, form, saveMutation, onSubmit, itemData } = useEditPage<
    ContentPageFormData,
    ContentPage
  >({
    resourceKey: "content-page",
    listKey: "content-pages",
    apiPath: "/api/admin/content-pages",
    schema: contentPageCreateSchema,
    defaultValues: {
      title: "",
      slug: "",
      perex: "",
      body: "",
      featuredImageId: null,
      destinationId: "",
      status: "draft",
      metaTitle: "",
      metaDescription: "",
    },
    navigateTo: "/content-pages",
    transformOnLoad: (p) => ({
      title: p.title ?? "",
      slug: p.slug ?? "",
      perex: p.perex ?? "",
      body: p.body ?? "",
      featuredImageId: p.featuredImageId ?? null,
      destinationId: p.destinationId ?? "",
      status: p.status ?? "draft",
      metaTitle: p.metaTitle ?? "",
      metaDescription: p.metaDescription ?? "",
    }),
    transformOnSave: (data) => ({
      ...data,
      // Prázdná volitelná pole ukládat jako null — web je pak bere jako „nevyplněno".
      perex: data.perex || null,
      body: data.body || null,
      destinationId: data.destinationId || null,
      metaTitle: data.metaTitle || null,
      metaDescription: data.metaDescription || null,
    }),
  });

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors, isSubmitting, isDirty },
  } = form;

  const statusLabels: Record<ContentPageStatus, string> = {
    draft: t("common.status.draft"),
    published: t("common.status.published"),
  };
  const slug = watch("slug");

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <EditPageHeader
        backUrl="/content-pages"
        isNew={isNew}
        newTitle={t("content.contentPages.form.newTitle")}
        editTitle={t("content.contentPages.form.editTitle")}
        entityName={itemData?.title}
        viewUrl={
          !isNew && slug && itemData?.status === "published"
            ? `${WEB_URL}${routes.stranka(slug)}`
            : undefined
        }
        formId="content-page-form"
        isSubmitting={isSubmitting}
        isDirty={isDirty}
        error={saveMutation.error}
        validationErrors={errors}
        isSuccess={saveMutation.isSuccess}
      />

      <form
        id="content-page-form"
        onSubmit={handleSubmit(onSubmit)}
        className="grid gap-6 lg:grid-cols-3"
      >
        <div className="space-y-6 lg:col-span-2">
          <div className="card space-y-4">
            <div>
              <label htmlFor="title" className="label">
                {t("common.fields.title")} *
              </label>
              <input
                {...register("title")}
                id="title"
                className={`input ${errors.title ? "input-error" : ""}`}
                placeholder={t("content.contentPages.form.titlePlaceholder")}
              />
              {errors.title && (
                <p className="text-error-600 mt-1 text-sm">{errors.title.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="slug" className="label">
                {t("content.contentPages.form.slugLabel")} *
              </label>
              <input
                {...register("slug")}
                id="slug"
                className={`input font-mono ${errors.slug ? "input-error" : ""}`}
              />
              {errors.slug ? (
                <p className="text-error-600 mt-1 text-sm">{errors.slug.message}</p>
              ) : (
                <p className="mt-1 text-sm text-gray-500">
                  {t("content.contentPages.form.slugHint", { path: routes.stranka(slug || "…") })}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="perex" className="label">
                {t("content.contentPages.form.perexLabel")}
              </label>
              <textarea
                {...register("perex")}
                id="perex"
                rows={3}
                maxLength={600}
                className={`input ${errors.perex ? "input-error" : ""}`}
                placeholder={t("content.contentPages.form.perexPlaceholder")}
              />
              {errors.perex && (
                <p className="text-error-600 mt-1 text-sm">{errors.perex.message}</p>
              )}
            </div>
          </div>

          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {t("content.contentPages.form.bodyLabel")}
            </h2>
            <Controller
              name="body"
              control={control}
              render={({ field }) => (
                <RichTextEditor
                  content={field.value || ""}
                  onChange={field.onChange}
                  placeholder={t("content.contentPages.form.bodyPlaceholder")}
                />
              )}
            />
          </div>

          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {t("content.shared.seoTitle")}
            </h2>
            <div className="grid gap-4">
              <div>
                <label htmlFor="metaTitle" className="label">
                  {t("content.shared.metaTitleLabel")}
                </label>
                <input
                  {...register("metaTitle")}
                  id="metaTitle"
                  maxLength={60}
                  className={`input ${errors.metaTitle ? "input-error" : ""}`}
                  placeholder={t("content.shared.metaTitlePlaceholder")}
                />
              </div>
              <div>
                <label htmlFor="metaDescription" className="label">
                  {t("content.shared.metaDescriptionLabel")}
                </label>
                <textarea
                  {...register("metaDescription")}
                  id="metaDescription"
                  rows={2}
                  maxLength={160}
                  className={`input ${errors.metaDescription ? "input-error" : ""}`}
                  placeholder={t("content.shared.metaDescriptionPlaceholder")}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="card space-y-4">
            <div>
              <label htmlFor="status" className="label">
                {t("common.fields.status")}
              </label>
              <select {...register("status")} id="status" className="input">
                {CONTENT_PAGE_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {statusLabels[status]}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-gray-500">
                {t("content.contentPages.form.statusHint")}
              </p>
            </div>

            <div>
              <label htmlFor="destinationId" className="label">
                {t("content.contentPages.form.destinationLabel")}
              </label>
              <Controller
                name="destinationId"
                control={control}
                render={({ field }) => (
                  <DestinationPicker
                    id="destinationId"
                    level="country"
                    value={field.value}
                    onChange={(id) => field.onChange(id ?? "")}
                  />
                )}
              />
              <p className="mt-1 text-sm text-gray-500">
                {t("content.contentPages.form.destinationHint")}
              </p>
            </div>
          </div>

          <div className="card">
            <label className="label">{t("content.contentPages.form.featuredImageLabel")}</label>
            <Controller
              name="featuredImageId"
              control={control}
              render={({ field }) => (
                <MediaPicker
                  purpose="hero"
                  mode="single"
                  value={field.value ?? null}
                  onChange={field.onChange}
                  folder="general"
                />
              )}
            />
          </div>
        </div>
      </form>
    </div>
  );
}
